import React, { Fragment, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import t from '@/utils/translate';

import { RootState, useAppSelector } from '@/state/store';
import { list as fetchProjects, logo as fetchLogo, thumbnail as fetchThumbnail } from '@/loaders/project';
import Apartment from '@mui/icons-material/Apartment';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Container, Image, Button, Link } from '@/components';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';

import Layout from '../Layout';
import {
  ProjectsGroup,
  ProjectsGroupBy,
  ProjectsOrderBy,
  RequestStatus
} from '@/contracts';
import InvitationsList from './InvitationsList';
import SortControl from './SortControl';
import { saveProjectsListSettings } from '@/actions/client';
import { Project } from '@bimdata/definitions/dto';
import { createSelector } from '@reduxjs/toolkit';
import type { ImplicitProjectThumbnails } from '@/state/authentication.slice';
import PageTitle from '@/components/PageTitle';

const FlexContainer = styled(Container)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'left',
  padding: '10px'
}));

const Item = styled(Card)(({ theme }) => ({
  ...theme.typography.body2,
  textAlign: 'center',
  color: theme.palette.text.secondary,
  width: '260px',
  height: '182px',
  margin: '10px',
  background: '#FFFFFF',
  boxShadow: '0px 0px 18px rgba(0, 0, 0, 0.1)',
  borderRadius: '8px'
}));

const ItemMedia = styled(CardMedia)(({ theme }) => ({
  width: '100%',
  height: '140px',
  overflow: 'hidden',
  position: 'relative',
  borderBottom: '1px solid #EBECF0'
}));

const ItemContent = styled('div')({
  height: '42px',
  padding: '0px',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '13px',
  lineHeight: '15px',
  color: '#344563',
  display: 'flex',
  justifyContent: 'space-between',
  flexWrap: 'nowrap',
  alignItems: 'center',
  verticalAlign: 'middle'
});

const ItemTitle = styled(Link)({
  maxWidth: '224px', 
  display: 'inline-block',
  paddingLeft: '12px',
  cursor: 'pointer',
  textWrap: 'nowrap',
  overflowX: 'clip',
  textOverflow: 'ellipsis',
  textAlign: 'left',
  height: '42px',
  lineHeight: '42px',
  flexGrow: 1,
});

const ItemKebab = styled(Link)({
  width: '36px',
  height: '42px',
  padding: '12px 9px',
  color: '#C1C7D0',
  '&:hover': {
    color: '#091E42',
  }
});

const AddNewProject = styled(Button)(({ theme }) => ({
  width: '260px',
  height: '182px',
  margin: '10px',
  display: 'block',
  border: '1px solid #EBECF0',
  borderRadius: '8px',
}));

const CustomAddCircleOutlineIcon = styled(AddCircleOutline)(({ theme }) => ({
  fontSize: theme.spacing(15),
}));

const HeaderAddProject = styled(Link)`
  :hover {
    background-color: #fafafa
  }
`;

const getProjectsGroupsFromList = (
  list: Project[],
  orderBy: ProjectsOrderBy,
  groupBy: ProjectsGroupBy,
  isDescOrder: boolean
) => {
  const groupsMap = (list || []).slice()
    .sort((a, b) => {
      let result = 0;
      switch (orderBy) {
        case ProjectsOrderBy.NAME:
          result = a.name.localeCompare(b.name);
          break;
        case ProjectsOrderBy.CREATED:
          result = a.created.getTime() - b.created.getTime();
          break;
        case ProjectsOrderBy.UPDATED:
          result = a.updated.getTime() - b.updated.getTime();
          break;
      }
      return result * (isDescOrder ? -1 : 1);
    })
    .reduce((acc, p) => {
      let groupName;
      switch (groupBy) {
        case ProjectsGroupBy.NONE:
        default: groupName = t('project.title.list');
      }
      let group = acc.get(groupName);
      if (!group) {
        group = {
          name: groupName,
          list: []
        };
        acc.set(groupName, group);
      }
      group.list.push(p);
      return acc;
    }, new Map<string, ProjectsGroup>());

  return Array.from(groupsMap.values())
    .sort((a, b) => {
      switch (groupBy) {
        case ProjectsGroupBy.NONE:
        default: return a.name.localeCompare(b.name);
      }
    });
};

const fetchLogos = async (
  projects: Project[],
  ImplicitProjectThumbnails: Record<string, ImplicitProjectThumbnails>,
  logos: Record<string, { status: RequestStatus }>,
  thumbnails: Record<string, { status: RequestStatus }>,
) => {
  for (const pd of projects) {
    const thumbnailsEnabled = (typeof ImplicitProjectThumbnails[pd.code]?.enabled === 'undefined')
      ? true
      : !!ImplicitProjectThumbnails[pd.code]?.enabled;
    if (
      thumbnailsEnabled
      && ImplicitProjectThumbnails[pd.code]?.small
      && (!thumbnails[pd.code] || thumbnails[pd.code].status === RequestStatus.ERROR)
    ) {
      await fetchThumbnail(pd.code, ImplicitProjectThumbnails[pd.code].small!);
    } else if (
      pd.logo
      && (!logos[pd.code] || logos[pd.code].status === RequestStatus.ERROR)
    ) {
      await fetchLogo(pd);
    }
  }
};

export default () => {
  const {
    list, status, logos, thumbnails, projectsOrderBy, projectsDescOrder, projectsGroupBy, ImplicitProjectThumbnails
  } = useAppSelector(
    createSelector(
      [
        (state: RootState) => state.projects.list,
        (state: RootState) => state.projects.listFetchStatus,
        (state: RootState) => state.projects.logos,
        (state: RootState) => state.projects.thumbnails,
        (state: RootState) => state.authentication.clientState.projectsOrderBy,
        (state: RootState) => state.authentication.clientState.projectsDescOrder,
        (state: RootState) => state.authentication.clientState.projectsGroupBy,
        (state: RootState) => state.authentication.clientState.ImplicitProjectThumbnails,
      ],
      (list, status, logos, thumbnails, projectsOrderBy, projectsDescOrder, projectsGroupBy, ImplicitProjectThumbnails) => ({
        list, status, logos, thumbnails, projectsOrderBy, projectsDescOrder, projectsGroupBy, ImplicitProjectThumbnails
      })
    )
  );

  useEffect(() => {
    if (status === RequestStatus.IDLE) {
      if (!Array.isArray(list)) {
        fetchProjects();
      } else {
        fetchLogos(list, ImplicitProjectThumbnails, logos, thumbnails);
      }
    }
  }, [status, list]);

  const groups = Array.isArray(list)
    ? getProjectsGroupsFromList(list, projectsOrderBy, projectsGroupBy, projectsDescOrder)
    : [];

  return (
    <Layout>
      <PageTitle>{t('project.title.list')}</PageTitle>
      {/*status === RequestStatus.BUSY && (
        <LinearProgress sx={{
          position: 'fixed',
          left: 0,
          right: 0
        }}/>
      )*/}

      <InvitationsList />
      <FlexContainer>
        {(groups || []).map(({ name, list }) => (<Fragment key={name}>
          <div style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              margin: '22px 10px 10px 10px',
              justifyContent: 'space-between',
            }}
          >
            <div style={{
                display: 'flex',
                gap: '6px',
                alignItems: 'center',
              }}
            >
              <h3 style={{
                fontStyle: 'normal',
                fontWeight: 500,
                fontSize: '20px',
                lineHeight: '23px',
                color: '#000000',
                margin: 0,
                padding: 0,
              }}>
                {name}
              </h3>
              {!!list.length && (
                <HeaderAddProject
                  to="./new"
                  title={t('project.action.createProject')}
                  style={{
                    width: '32px',
                    height: '32px',
                    lineHeight: '32px',
                    textAlign: 'center',
                  }}
                >
                  <AddCircleOutline style={{
                    fontSize: '24px',
                    color: '#0052CC',
                    margin: '0px',
                    verticalAlign: 'middle'
                  }}/>
              </HeaderAddProject>
              )}
            </div>
            {list.length > 1 && (
              <SortControl
                orderBy={projectsOrderBy}
                isDescOrder={projectsDescOrder}
                onChange={saveProjectsListSettings}
              />
            )}
          </div>
          {(list || []).map((p) => {
            const thumbnailsEnabled = (typeof ImplicitProjectThumbnails[p.code]?.enabled === 'undefined')
              ? true
              : !!ImplicitProjectThumbnails[p.code]?.enabled;
            return <Item key={p.code}>
              <Link to={`/project/${p.code}`}>
                <ItemMedia>
                  <Image
                    sizeV={140}
                    source={
                      (thumbnailsEnabled && thumbnails[p.code]?.source)
                      || logos[p.code]?.source
                    }
                    placeholder={<Apartment style={{ fontSize: '32px', verticalAlign: 'inherit' }}/>}
                  />
                </ItemMedia>
              </Link>
              <ItemContent>
                <ItemTitle to={`/project/${p.code}`}>{ p.name }</ItemTitle>
                <ItemKebab to={`/project/${p.code}/settings`}>
                  <MoreVertIcon />
                </ItemKebab>
              </ItemContent>
            </Item>
          })}
        </Fragment>))}

        <Link to="./new">
          <AddNewProject>
            <Container>
              <CustomAddCircleOutlineIcon/>
            </Container>
            <Container margin-v='small'>{t('project.action.createProject')}</Container>
          </AddNewProject>
        </Link>
      </FlexContainer>
    </Layout>
  );
}